import { useState } from 'react';

import { trackUmamiEvent } from '@/lib/analytics';

export type Reference = {
  title: string;
  source: string;
  url?: string;
};

interface Props {
  references: Reference[];
}

interface FaviconCircleProps {
  url?: string;
  size?: number;
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
}

function FaviconCircle({ url, size = 30 }: FaviconCircleProps) {
  const [failed, setFailed] = useState(false);
  const domain = url ? getDomain(url) : '';
  const fallback = domain ? domain[0].toUpperCase() : '📖';

  return (
    <div
      className="border-line bg-background relative flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full border"
      style={{ width: size, height: size }}
    >
      {!failed && domain ? (
        <img
          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
          alt=""
          loading="lazy"
          decoding="async"
          className="object-contain"
          style={{ width: size * 0.53, height: size * 0.53 }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="text-on-surface-light font-bold" style={{ fontSize: size * 0.37 }}>
          {fallback}
        </span>
      )}
    </div>
  );
}

export default function TimelineReferences({ references }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  if (!references.length) return null;

  return (
    <div className="border-line mt-6 border-t pt-4">
      {/* Trigger row */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls="timeline-refs-list"
        className="group flex cursor-pointer items-center gap-3 border-0 bg-transparent p-0"
      >
        <div className="flex items-center">
          {references.map((ref, i) => (
            <div key={ref.title} style={{ marginLeft: i === 0 ? 0 : -9 }}>
              <FaviconCircle url={ref.url} size={30} />
            </div>
          ))}
        </div>
        <span className="text-on-surface-light group-hover:text-on-surface text-xs font-medium transition-colors">
          {references.length} referências
        </span>
        <svg
          className={`text-on-surface-light h-3.5 w-3.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
        >
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>

      {/* Expandable list */}
      <div
        id="timeline-refs-list"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'mt-3 max-h-[600px] opacity-100' : 'mt-0 max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-col gap-1">
          {references.map((ref) =>
            ref.url ? (
              <a
                key={ref.title}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackUmamiEvent('external_link_click', {
                    target: ref.url,
                    label: ref.title,
                    source: 'history_timeline',
                  })
                }
                className="group/item border-line bg-surface hover:border-line-strong flex items-center gap-2.5 rounded-md border px-3 py-2.5 no-underline transition-all duration-150 hover:translate-x-[3px]"
              >
                <FaviconCircle url={ref.url} size={26} />
                <div className="min-w-0 flex-1">
                  <div className="text-on-surface truncate text-xs font-semibold">{ref.title}</div>
                  <div className="text-on-surface-light mt-0.5 text-[11px]">{ref.source}</div>
                </div>
                <svg
                  className="text-on-surface-light/60 group-hover/item:text-primary h-3 w-3 flex-shrink-0 transition-colors"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 2H2a1 1 0 00-1 1v7a1 1 0 001 1h7a1 1 0 001-1V7M7 1h4m0 0v4m0-4L5 7" />
                </svg>
              </a>
            ) : (
              <div
                key={ref.title}
                className="border-line bg-surface flex items-center gap-2.5 rounded-md border px-3 py-2.5 opacity-50"
              >
                <FaviconCircle size={26} />
                <div className="min-w-0 flex-1">
                  <div className="text-on-surface truncate text-xs font-semibold">{ref.title}</div>
                  <div className="text-on-surface-light mt-0.5 text-[11px]">{ref.source}</div>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
